import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import * as htmlToImage from 'html-to-image'
import BugReportModal from '@/components/BugReportModal'
import { apiService } from '@/service/api'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { useTranslation } from '@/i18n'
import { BugReportContext } from './bugReportContext'
import { formatRecentCorrelationIds, formatRecentErrors } from '@/utils/bugReportDiagnostics'
import { isCapturableNode, onCaptureImageError, describeCaptureFailure } from './captureFilter'
import { resolveReporterTimezone } from './resolveReporterTimezone'

export function BugReportProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const { user } = useAppStore()
  // Annotators may file bugs, but must not be able to preview/download the
  // captured screenshot — it's a P&ID exfiltration vector (Armin/Pegah, Slack).
  // The screenshot is still captured + attached to the Linear issue below;
  // only its in-app preview is suppressed for this role.
  const hideScreenshotFromAnnotator = isAnnotatorRole(user?.role)

  const [isOpen, setIsOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null)

  const openBugReport = useCallback(async () => {
    // Capture screenshot BEFORE opening the modal so the popup doesn't block the view
    try {
      const dataUrl = await htmlToImage.toJpeg(document.body, {
        quality: 0.6,
        filter: isCapturableNode,
        // One unloadable image must degrade the screenshot, not lose it
        onImageErrorHandler: onCaptureImageError,
      })
      setScreenshotDataUrl(dataUrl)
    } catch (err) {
      console.warn('Failed to capture screenshot for bug report', describeCaptureFailure(err))
    }

    setIsOpen(true)
  }, [])

  const closeBugReport = useCallback(() => {
    setIsOpen(false)
    setScreenshotDataUrl(null)
  }, [])

  const value = useMemo(() => ({ openBugReport }), [openBugReport])

  return (
    <BugReportContext.Provider value={value}>
      {children}
      <BugReportModal
        isOpen={isOpen}
        onClose={closeBugReport}
        screenshotDataUrl={screenshotDataUrl}
        onRemoveScreenshot={() => setScreenshotDataUrl(null)}
        hideScreenshotPreview={hideScreenshotFromAnnotator}
        screenshotHiddenLabel={t('common:bugReport.screenshot_hidden', 'Screenshot attached to your report')}
        onSubmit={async ({ name, email, message, attachments }) => {
          setIsSubmitting(true)
          try {
            // Collect diagnostics: recent correlation IDs and errors
            const correlationIds = formatRecentCorrelationIds()
            const stackTrace = formatRecentErrors()

            // Where the reporter is, for the ETA in the acknowledgement email
            // (ENG-2936). The PRD states it as an absolute local time and rules
            // out "in 4 hours", so this is a required input, not a nicety.
            //
            // Read from the browser rather than the user profile: profile
            // timezone is optional, so it is set for some people and not others
            // and silently yields a mix of correct and defaulted times with
            // nothing to tell them apart. The browser answers for everyone, and
            // says where the reporter actually IS rather than where they once
            // said they were. Empty falls back to the tenant default server-side.
            const timezone = resolveReporterTimezone()

            const result = await apiService.submitFeedback({
              name,
              email,
              message,
              url: window.location.href,
              userAgent: navigator.userAgent,
              correlationIds: correlationIds || undefined,
              stackTrace: stackTrace || undefined,
              timezone,
              attachments,
            })
            if (!result.success) {
              throw new Error(result.error || 'Failed to submit feedback')
            }
            toast.success(t('common:bugReport.success_message', 'We received your feedback. Thanks for reaching out.'))
          } catch (e) {
            toast.error(t('common:bugReport.error_generic'))
            throw e
          } finally {
            setIsSubmitting(false)
          }
        }}
        defaultName={user?.name}
        defaultEmail={user?.email}
        title={t('common:bugReport.dialog_title', 'Report a bug')}
        subtitle={t('common:bugReport.dialog_subtitle', "A screenshot was captured when you clicked 'Report a bug'")}
        labelName={t('common:bugReport.label_name')}
        labelEmail={t('common:bugReport.label_email')}
        labelComments={t('common:bugReport.label_comments')}
        placeholderComments={t('common:bugReport.placeholder_comments', 'Tell us more about the issue you encountered')}
        labelAttachments={t('common:bugReport.attachments_label')}
        labelAttachMedia={t('common:bugReport.attachments_add_button', 'Attach Media')}
        getTooManyAttachmentsError={(max) =>
          t('common:bugReport.attachments_error_too_many', { max })
        }
        getFileTooLargeError={(fileName, maxMb) =>
          t('common:bugReport.attachments_error_too_large', { fileName, maxMb })
        }
        labelClose={t('common:bugReport.label_close')}
        labelSubmit={t('common:bugReport.label_submit', 'Send')}
        labelSubmitting={t('common:bugReport.submitting')}
        isLoading={isSubmitting}
      />
    </BugReportContext.Provider>
  )
}
